import { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import SlicerFilamentForm from '../components/SlicerFilamentForm';
import PageHeader from '../components/ui/PageHeader';

export default function SlicerFilamentSettings() {
  const [filaments, setFilaments] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const fetchFilaments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/auth/slicer-settings/filaments');
      setFilaments(res.data.data || []);
    } catch {
      console.error('Failed to fetch filament settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFilaments(); }, []);

  useEffect(() => {
    let result = [...filaments];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(f => f.name?.toLowerCase().includes(s) || f.uid?.toLowerCase().includes(s));
    }
    setFiltered(result);
  }, [filaments, search]);

  const handleSave = async (data) => {
    if (editing) {
      await api.patch(`/auth/slicer-settings/filaments/${editing.uid}`, data);
    } else {
      await api.post('/auth/slicer-settings/filaments', data);
    }
    setShowForm(false);
    setEditing(null);
    fetchFilaments();
  };

  const handleDelete = async (filament) => {
    if (!window.confirm(`Deactivate slicer settings for "${filament.name}"?`)) return;
    try {
      await api.patch(`/auth/slicer-settings/filaments/${filament.uid}`, { isActive: false });
      fetchFilaments();
    } catch {
      alert('Failed to deactivate');
    }
  };

  const columns = [
    { key: 'uid', label: 'UID', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'material', label: 'Material', render: (val) => typeof val === 'object' ? val.name : 'N/A' },
    { key: 'materialExtra', label: 'Extra', render: (val) => typeof val === 'object' && val !== null ? val.key : '-' },
    { key: 'settings', label: 'Configured machines', render: (val) => Array.isArray(val) ? val.length : 0 },
    { key: 'isActive', label: 'Active', render: (val) => val ? '✓' : '✗' },
    { key: 'createdAt', label: 'Created', sortable: true, render: (val) => new Date(val).toLocaleDateString() },
  ];

  if (loading && filaments.length === 0) return <div className="text-center py-8 text-sm text-slate-500">Loading...</div>;

  return (
    <div className="space-y-4">
      <PageHeader title="Filament Settings" description="Slicer configuration per filament type" />

      {showForm ? (
        <SlicerFilamentForm filament={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} />
      ) : (
        <DataTable
          data={filtered}
          columns={columns}
          onAdd={() => { setEditing(null); setShowForm(true); }}
          onEdit={(f) => { setEditing(f); setShowForm(true); }}
          onDelete={handleDelete}
          renderFilters={() => (
            <input
              type="text"
              placeholder="Search by UID or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-4 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          )}
        />
      )}
    </div>
  );
}
